/**
 * Cross-tab tenant-divergence guard (adversarial review P0-3).
 *
 * ## The hazard
 *
 * Two different things decide "which tenant" on a proxied call:
 *
 * - the **query key** prefix, which is a React prop (`AppShell activeTenant`)
 *   captured when the layout rendered — i.e. per browser tab, frozen;
 * - the **request**, which the BFF proxy stamps from `session.activeTenant` —
 *   i.e. ONE shared Redis record for every tab of the browser.
 *
 * `POST /api/auth/tenant` mutates the shared record and only `router.refresh()`es
 * the switching tab. So tab A (rendered for `acme`) keeps issuing requests that
 * the proxy silently retargets at `beta`: beta's rows land under `["acme", …]`,
 * and the next save `PUT`s into the **beta database** while the chrome says acme.
 * Before the `(app)` route group this self-healed on any cross-area navigation
 * (six layouts each re-read the session); with a single layout that never
 * re-renders on navigation, a diverged tab never recovers on its own.
 *
 * ## The fix, and why this one
 *
 * Three candidates were considered:
 *
 * 1. **Make the active tenant genuinely per-tab.** Correct in principle, but the
 *    tenant is also read by *server* components (layout, branding, prefetch), so
 *    it would have to ride in the URL or a per-tab cookie — neither exists, and a
 *    per-tab cookie is a contradiction in terms. A large, risky redesign.
 * 2. **Cross-tab broadcast only.** Helps, but is advisory: a tab with the channel
 *    unavailable (older Safari, a paused/bfcached tab, a broadcast lost during a
 *    reload) keeps writing to the wrong database. It narrows the window; it does
 *    not close it.
 * 3. **Assert the expectation at the trust boundary** — the client stamps the
 *    tenant its cache is keyed by on every proxied request, and the BFF **fails
 *    the request** when that disagrees with `session.activeTenant`.
 *
 * (3) is the only one that makes the wrong-tenant write *impossible* rather than
 * *unlikely*, and it is enforced on the server, where the retarget happened. It
 * is also cheap and directly testable. We ship (3) as the hard guarantee **and**
 * (2) on top of it, so the diverged tab does not merely start erroring — it
 * notices and reloads into the correct tenant.
 *
 * The failure mode is deliberately loud and recoverable: HTTP **409** with
 * `title: "tenant_mismatch"`, no backend call at all, and an
 * `auth:tenant-mismatch` DOM event that the shell turns into a reload. Nothing
 * is silently retargeted, and nothing is silently dropped.
 *
 * This module is intentionally free of React and of `server-only`: the constants
 * are shared by the browser client and the BFF route handler.
 */

/**
 * Header the browser stamps on every `/api/proxy/*` call, carrying the tenant
 * slug its TanStack query keys are prefixed with.
 *
 * Note this is an *assertion of expectation*, never an authorization input: the
 * proxy only ever compares it to `session.activeTenant` and refuses on
 * disagreement. It can never widen access, so a forged value gains nothing.
 */
export const EXPECTED_TENANT_HEADER = "X-IsmsHub-Expected-Tenant";

/** `title`/`code` of the 409 the proxy answers on a divergence. */
export const TENANT_MISMATCH_CODE = "tenant_mismatch";

/** DOM event the api client fires when the proxy reports a divergence. */
export const TENANT_MISMATCH_EVENT = "auth:tenant-mismatch";

/** BroadcastChannel name used to tell sibling tabs that the session tenant moved. */
export const TENANT_SWITCH_CHANNEL = "ismshub:tenant";

/**
 * The tenant this tab believes is active. Module state is exactly the right
 * scope: one JS realm == one tab, which is the granularity the query cache
 * already has and the Redis session does not.
 */
let expectedTenant = "";

/**
 * Publishes the tab's active tenant. Called by `TenantProvider` during render so
 * the value is in place before any query can fire.
 *
 * No-op on the server: module scope is shared across concurrent requests there,
 * and nothing server-side goes through the browser client anyway (the layout
 * prefetch talks to the backend directly with the session's tenant).
 */
export function setExpectedTenant(slug: string): void {
  if (typeof window === "undefined") return;
  expectedTenant = slug;
}

/** The tenant this tab expects, or `""` before a provider has published one. */
export function getExpectedTenant(): string {
  return expectedTenant;
}

/** Test seam: forget the published tenant. */
export function resetExpectedTenant(): void {
  expectedTenant = "";
}

/**
 * Adds the expectation header to an outgoing proxy request. Omitted when the tab
 * has no tenant yet (pre-shell calls, membership-less operator) — the proxy then
 * behaves exactly as before, which is correct: there is no expectation to break.
 */
export function withExpectedTenantHeader(
  headers: Record<string, string> = {},
): Record<string, string> {
  const slug = getExpectedTenant();
  return slug ? { ...headers, [EXPECTED_TENANT_HEADER]: slug } : headers;
}

/** True when `body` is the proxy's tenant-divergence problem document. */
export function isTenantMismatchBody(body: unknown): boolean {
  if (!body || typeof body !== "object") return false;
  const doc = body as { title?: unknown; code?: unknown };
  return doc.title === TENANT_MISMATCH_CODE || doc.code === TENANT_MISMATCH_CODE;
}
